import React, { useState, useCallback } from 'react';
import {
  Drawer,
  Divider,
  IconButton,
  List,
  Tooltip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ClickAwayListener,
  Box,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import TagOutlinedIcon from '@mui/icons-material/TagOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import SvgIcon from '@mui/material/SvgIcon';
import TaskNotificationsBadge from './TaskNotificationsBadge';
import SidebarUsageDisplay from './SidebarUsageDisplay';
import HelpDialog from './HelpDialog';
import { AI_AGENTS_CLIENT_IDS, AI_CASES_PLAYBOOKS_CLIENT_IDS } from '../../constants/featureFlags';

const DRAWER_WIDTH = 204;
const DRAWER_COLLAPSED_WIDTH = 48;

function WorkflowLogo(props) {
  return (
    <SvgIcon
      viewBox="0 0 1024 1024"
      preserveAspectRatio="xMinYMin meet"
      {...props}
      sx={{ color: 'inherit', borderRadius: '6px' }}
    >
      <rect width="1024" height="1024" rx="80" fill="#141414" />
      <rect x="176" y="176" width="288" height="288" rx="32" fill="#7AC0F8" />
      <rect x="560" y="176" width="288" height="288" rx="144" fill="#2670FF" />
      <path
        d="M529.5 320H494.5"
        stroke="#B3B3B3"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="bevel"
      />
      <rect x="176" y="560" width="288" height="288" rx="32" fill="#FFB74D" />
      <rect x="560" y="560" width="288" height="288" rx="32" fill="#E10050" />
      <path
        d="M529.5 704V704C519.835 704 512 696.165 512 686.5V337.5C512 327.835 504.165 320 494.5 320V320"
        stroke="#B3B3B3"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="bevel"
      />
    </SvgIcon>
  );
}

const DrawerItem = ({ selected, onClick, title, icon, id, nested }) => (
  <ListItemButton
    id={id}
    selected={selected}
    onClick={onClick}
    sx={{
      borderLeft: selected ? '3px solid' : '3px solid transparent',
      borderLeftColor: selected ? 'text.primary' : 'transparent',
      height: nested ? 34 : 42,
      transition: 'all 0.3s ease',
      '&:hover': {
        bgcolor: 'action.hover',
        '& .drawer-icon': { transform: 'scale(1.15)', color: 'text.primary' },
        '& .drawer-text': { transform: 'translateX(3px)', color: 'text.primary' },
      },
      '&.Mui-selected': {
        bgcolor: 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
      },
    }}
  >
    <ListItemIcon
      className="drawer-icon"
      sx={{
        color: selected ? 'text.primary' : 'text.disabled',
        fontSize: nested ? 18 : 20,
        ml: nested ? '24px' : '10px',
        mr: nested ? '12px' : '16px',
        minWidth: 0,
        transition: 'color 0.3s ease, transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        '& .MuiSvgIcon-root': nested ? { fontSize: 18 } : undefined,
      }}
    >
      {icon}
    </ListItemIcon>
    <ListItemText
      disableTypography
      primary={
        <h4
          className="drawer-text"
          style={{
            fontFamily: 'Open Sans',
            fontWeight: selected ? 500 : 'normal',
            fontSize: nested ? 13 : 14,
            margin: 0,
            color: selected ? 'var(--color-text-primary)' : 'var(--color-text-disabled)',
            transition: 'transform 0.3s ease, color 0.3s ease',
          }}
        >
          {title}
        </h4>
      }
    />
  </ListItemButton>
);

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})(({ theme, isOpen }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    width: isOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    whiteSpace: 'nowrap',
    '& .MuiButtonBase-root': {
      paddingLeft: 0,
      paddingRight: 0,
    },
    fontSize: 12,
  },
}));

const RESOURCE_PATHS = [
  '/project', '/databases', '/schedules', '/files',
  '/global_variables', '/credential_store', '/project/trash',
];

const getMainMenuItems = ({ roleEditor, roleAdministrator, rolePrivateViewer, clientId }) => [
  {
    path: '/',
    label: 'Home',
    icon: <HomeOutlinedIcon />,
    tooltip: 'Your home page',
    visible: roleEditor || rolePrivateViewer,
    exact: true,
    id: 'main_menu_drawer_home',
  },
  {
    path: '/channels',
    label: 'Channels',
    icon: <TagOutlinedIcon />,
    tooltip: 'Agent channels and conversations',
    visible: (roleEditor || roleAdministrator) && AI_AGENTS_CLIENT_IDS.includes(clientId),
  },
  {
    path: '/agents',
    label: 'Agents',
    icon: <SmartToyOutlinedIcon />,
    tooltip: 'Manage your AI agents',
    visible: (roleEditor || roleAdministrator) && AI_AGENTS_CLIENT_IDS.includes(clientId),
  },
  {
    path: '/task',
    label: 'Tasks',
    icon: <TaskNotificationsBadge />,
    tooltip: 'Manage your assigned tasks',
    visible: true,
  },
  {
    path: '/user_management',
    label: 'Humans',
    icon: <GroupsOutlinedIcon />,
    tooltip: 'Manage users in your account',
    visible: roleAdministrator,
  },
  {
    path: '/resources',
    label: 'Resources',
    icon: <WidgetsOutlinedIcon />,
    tooltip: 'Workflows, tables, schedules, files, and more',
    visible: roleEditor || roleAdministrator,
    isResourceGroup: true,
    matchPaths: ['/resources', ...RESOURCE_PATHS],
    excludePaths: ['/project/canvas', '/project/canvas_preview'],
    children: [
      {
        path: '/project',
        label: 'Workflows',
        icon: <AccountTreeOutlinedIcon />,
        tooltip: 'All of your published and unpublished workflows',
        visible: roleEditor,
        matchPaths: ['/project'],
        excludePaths: ['/project/trash', '/project/canvas', '/project/canvas_preview'],
      },
      {
        path: '/databases',
        label: 'Tables',
        icon: <StorageOutlinedIcon />,
        tooltip: 'Access your built-in tables',
        visible: roleEditor || roleAdministrator,
      },
      {
        path: '/schedules',
        label: 'Schedules',
        icon: <ScheduleOutlinedIcon />,
        tooltip: 'View all scheduled runs across your workflows',
        visible: roleEditor || roleAdministrator,
      },
      {
        path: '/files',
        label: 'Files',
        icon: <FolderOutlinedIcon />,
        tooltip: 'View all files across your workflows',
        visible: roleEditor || roleAdministrator,
      },
      {
        path: '/global_variables',
        label: 'Global Variables',
        icon: <DataObjectOutlinedIcon />,
        tooltip: 'Manage account-level variables accessible in all workflows',
        visible: roleEditor || roleAdministrator,
      },
      {
        path: '/credential_store',
        label: 'Credentials',
        icon: <VpnKeyOutlinedIcon />,
        tooltip: 'View your securely stored credentials and secrets',
        visible: roleEditor || roleAdministrator,
      },
      {
        path: '/project/trash',
        label: 'Trash',
        icon: <DeleteOutlineOutlinedIcon />,
        tooltip: 'View deleted workflows',
        visible: roleEditor || roleAdministrator,
      },
    ],
  },
  {
    path: '/cases',
    label: 'Experimental',
    icon: <ScienceOutlinedIcon />,
    tooltip: 'Experimental features',
    visible: (roleEditor || roleAdministrator) && AI_AGENTS_CLIENT_IDS.includes(clientId),
    isResourceGroup: true,
    matchPaths: ['/cases', '/playbooks'],
    children: [
      {
        path: '/cases',
        label: 'Cases',
        icon: <WorkOutlineIcon />,
        tooltip: 'Active and historical agent cases',
        visible: (roleEditor || roleAdministrator) && AI_AGENTS_CLIENT_IDS.includes(clientId),
        matchPaths: ['/cases'],
      },
      {
        path: '/playbooks',
        label: 'Playbooks',
        icon: <ArticleOutlinedIcon />,
        tooltip: 'Playbook library',
        visible: (roleEditor || roleAdministrator) && AI_AGENTS_CLIENT_IDS.includes(clientId),
        matchPaths: ['/playbooks'],
      },
    ],
  },
];

function isItemActive(item, pathname) {
  if (item.exact) {
    return pathname === item.path;
  }

  if (item.excludePaths) {
    for (const excluded of item.excludePaths) {
      if (pathname.startsWith(excluded)) return false;
    }
  }

  if (item.matchPaths) {
    return item.matchPaths.some((mp) => pathname.startsWith(mp));
  }

  return pathname.startsWith(item.path);
}

export default function Sidebar({
  roleAdministrator,
  roleEditor,
  rolePrivateViewer,
  clientId,
  tier,
  userDisplayName,
  orgName,
  onSignOut,
}) {
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const location = useLocation();

  const handleDrawerOpen = useCallback(() => setOpen(true), []);
  const handleDrawerClose = useCallback(() => setOpen(false), []);

  const mainItems = getMainMenuItems({ roleEditor, roleAdministrator, rolePrivateViewer, clientId });

  return (
    <>
      <HelpDialog open={helpOpen} close={() => setHelpOpen(false)} />
      <StyledDrawer
        variant="permanent"
        isOpen={open}
        className="drawerMain"
        id="main_menu_drawer"
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'flex-end' : 'center',
          px: '12px',
          minHeight: '56px',
          maxHeight: '56px',
        }}>
          {open ? (
            <ClickAwayListener onClickAway={handleDrawerClose}>
              <IconButton onClick={handleDrawerClose} sx={{ width: 40, height: 40, borderRadius: '8px' }}>
                <ChevronLeftIcon />
              </IconButton>
            </ClickAwayListener>
          ) : (
            <IconButton aria-label="Open drawer" onClick={handleDrawerOpen} sx={{ width: 40, height: 40, borderRadius: '8px' }}>
              <WorkflowLogo />
            </IconButton>
          )}
        </Box>
        <Divider />
        <List sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          ...(tier === 'FREE' && { paddingBottom: '32px' }),
        }}>
          <Box>
            {mainItems.filter((item) => item.visible).map((item) => (
              <React.Fragment key={item.path}>
                {item.isResourceGroup ? (
                  <>
                    {/* Resources parent item — always shown */}
                    <Tooltip placement="right" title={item.tooltip}>
                      <Link to={item.path} style={{ textDecoration: 'none', margin: 0, padding: 0 }}>
                        <DrawerItem
                          selected={isItemActive(item, location.pathname)}
                          title={item.label}
                          icon={item.icon}
                        />
                      </Link>
                    </Tooltip>
                    {/* When expanded, show indented child items */}
                    {open && item.children && item.children.filter((c) => c.visible).map((child) => (
                      <Tooltip key={child.path} placement="right" title={child.tooltip}>
                        <Link to={child.path} style={{ textDecoration: 'none', margin: 0, padding: 0 }}>
                          <DrawerItem
                            nested
                            selected={isItemActive(child, location.pathname)}
                            title={child.label}
                            icon={child.icon}
                          />
                        </Link>
                      </Tooltip>
                    ))}
                  </>
                ) : (
                  <Tooltip placement="right" title={item.tooltip}>
                    <Link to={item.path} style={{ textDecoration: 'none', margin: 0, padding: 0 }}>
                      <DrawerItem
                        id={item.id}
                        selected={isItemActive(item, location.pathname)}
                        title={item.label}
                        icon={item.icon}
                      />
                    </Link>
                  </Tooltip>
                )}
              </React.Fragment>
            ))}
            <Tooltip placement="right" title="Help">
              <Link to="#" style={{ textDecoration: 'none' }}>
                <DrawerItem
                  selected={false}
                  onClick={() => setHelpOpen(true)}
                  title="Help"
                  icon={<HelpOutlineOutlinedIcon />}
                />
              </Link>
            </Tooltip>
          </Box>
          <Box>
            <SidebarUsageDisplay isOpen={open} />

            <Tooltip placement="right" title="Your user account settings">
              <Link to="/profile" style={{ textDecoration: 'none' }}>
                <DrawerItem
                  selected={location.pathname === '/profile'}
                  title={userDisplayName || ''}
                  icon={<AccountBoxOutlinedIcon />}
                />
              </Link>
            </Tooltip>
            {roleAdministrator && (
              <Tooltip placement="right" title="Your organization settings">
                <Link to="/organization" style={{ textDecoration: 'none' }}>
                  <DrawerItem
                    selected={location.pathname.startsWith('/organization')}
                    title={orgName || ''}
                    icon={<BusinessIcon />}
                  />
                </Link>
              </Tooltip>
            )}
            <Tooltip placement="right" title="Log out">
              <Link to="" style={{ textDecoration: 'none' }}>
                <DrawerItem
                  selected={false}
                  onClick={onSignOut}
                  title="Log out"
                  icon={<ExitToAppIcon />}
                />
              </Link>
            </Tooltip>
          </Box>
        </List>
      </StyledDrawer>
    </>
  );
}
